import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useIntl } from 'react-intl';

import useApprovalsForPay, { filterOptions } from '../../hooks/useApprovalsForPay';

import FormControl from '@mui/material/FormControl';
import Autocomplete from 'pages/components/ApproversAutocomplete';
import TextField from '@mui/material/TextField';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Check from '@mui/icons-material/Check';
import SortableListRaw from 'pages/document/DocumentTopPanel/approval/dnd/SortableList';
const SortableList = SortableListRaw as any;
import Stack from '@mui/material/Stack';
import Approver from 'pages/components/Approver';
import Avatar from '@mui/material/Avatar';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextFieldBase from 'components/mui/Form/TextField/TextFieldBase';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import SvgIcon from '@mui/material/SvgIcon';

import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import { ReactComponent as ParallelIcon } from './icons/ParallelArrows.svg';
import { ReactComponent as SequenceIcon } from './icons/SequenceArrows.svg';

import { ToggleButtonGroup, SMenuItem } from './StyledComponents';

import { Fields } from 'pages/configurations/company/pages/approvals/pages/advancedGroups/helpers';
import { LEVEL_FLOW_TYPE } from 'domain/approvals/constatns';

import elements from 'components/elements';

interface LevelItemProps {
  levelData: Record<string, any>;
  isSubmitting: boolean;
  setTouched: (touched: any) => void;
  touched: Record<string, any>;
  errors: Record<string, any>;
  handleLevelChange: (index: number, field: string, value: unknown) => void;
  removeLevel: (index: number) => void;
  i: number;
  canBeDelete: boolean;
  users: any[];
}

const LevelItem: React.FC<LevelItemProps> = ({
  levelData,
  isSubmitting,
  setTouched,
  touched,
  errors,
  handleLevelChange,
  removeLevel,
  i,
  canBeDelete,
  users,
}) => {
  const { formatMessage } = useIntl();
  const [focusedApproversContainer, setFocusedApproversContainer] = useState(false);
  const [mustBeAllMinApproversByDefault, setMustBeAllMinApproversByDefault] = useState(
    !levelData[Fields.approvers].length || levelData[Fields.approvers].length === levelData[Fields.minApprovers],
  );
  const [previewTags, setPreviewTags] = useState<any[]>([]);
  const approversContainerRef = useRef<HTMLDivElement | null>(null);

  const { approvers, value: approversValue } = useApprovalsForPay(levelData[Fields.approvers], users);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: levelData.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleAutocompleteChange = useCallback(
    (name: any) => (_e: unknown, value: any[]) => {
      const values = value.reduce((acc: any[], option: any) => {
        acc.push(option);
        return acc;
      }, []);
      handleLevelChange(i, name, values);
      if (!touched[Fields.levels][i][Fields.minApprovers] || levelData[Fields.minApprovers] > values.length) {
        handleLevelChange(
          i,
          Fields.minApprovers,
          mustBeAllMinApproversByDefault || levelData[Fields.minApprovers] > values.length
            ? values.length || 1
            : levelData[Fields.minApprovers],
        );
      }
      if (values.length <= 1) {
        handleLevelChange(i, Fields.flowType, LEVEL_FLOW_TYPE.SEQUENTIAL);
      }
    },
    [handleLevelChange, i, touched, levelData, mustBeAllMinApproversByDefault],
  );

  const handleDelete = useCallback(
    (id: string) => {
      handleAutocompleteChange([Fields.approvers])(
        undefined,
        levelData[Fields.approvers].filter((i: any) => i.id !== id),
      );
    },
    [handleAutocompleteChange, levelData],
  );

  const handleFocusApproversField = useCallback(() => {
    setFocusedApproversContainer(true);
  }, []);

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name?: string } }) => {
      if (e.target.name) {
        const updatedLevels = [...touched[Fields.levels]];
        updatedLevels[i][e.target.name] = true;
        setTouched({ ...touched, [Fields.levels]: updatedLevels });
      }
      setFocusedApproversContainer(false);
    },
    [setTouched, i, touched],
  );

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      handleLevelChange(i, Fields.approvers, arrayMove(levelData[Fields.approvers], oldIndex, newIndex));
    },
    [levelData, handleLevelChange, i],
  );

  const tileSize = useMemo(() => [...approversValue].splice(previewTags.length).length, [previewTags, approversValue]);

  const handleFlowTypeClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, flow: string | null) => {
      e.preventDefault();
      if (flow !== null) {
        handleLevelChange(i, Fields.flowType, flow);
      }
    },
    [handleLevelChange, i],
  );

  useEffect(() => {
    const tags: any[] = [];
    if (!focusedApproversContainer && approversContainerRef.current) {
      const wrapperWidth = approversContainerRef.current.getBoundingClientRect().width;
      let tw = 40; // approximate chip width
      approversValue.forEach((tag) => {
        const string = tag.fullName;
        tw += string.length * 6 + 48;
        // tags.length === 0 - always render first tag
        if (tw < wrapperWidth || tags.length === 0) {
          tags.push(tag);
        }
      });
    }
    setPreviewTags(tags);
  }, [approversValue, focusedApproversContainer, approversContainerRef]);

  return (
    <Stack direction="row" alignItems="flex-start" gap={2} my={2} ref={setNodeRef} style={style} {...attributes}>
      {canBeDelete && (
        <DragIndicatorOutlinedIcon
          {...listeners}
          sx={{ color: 'grey.600', cursor: isDragging ? 'grabbing' : 'grab', mt: 2, mr: -2 }}
        />
      )}
      <FormControl fullWidth>
        <Autocomplete
          fullWidth
          required
          margin="normal"
          disabled={isSubmitting}
          data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.selectApprovers}
          noOptionsText={formatMessage({
            id: 'document.approval.noSuggestions',
            defaultMessage: 'No company users available',
          })}
          options={approvers}
          value={approversValue}
          onChange={handleAutocompleteChange(Fields.approvers)}
          onBlur={onBlur}
          onFocus={handleFocusApproversField}
          getOptionLabel={(o: any) => o.id}
          filterOptions={filterOptions}
          renderInput={(params: any) => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                sx: { flexWrap: `${focusedApproversContainer ? 'wrap' : 'nowrap'} !important` },
              }}
              name={Fields.approvers}
              error={Boolean(
                errors[Fields.levels][i]?.[Fields.approvers] && touched[Fields.levels][i]?.[Fields.approvers],
              )}
              required
              label={formatMessage({
                id: 'configurations.company.approvals.advancedGroups.form.approvers.label',
                defaultMessage: 'Approvers',
              })}
            />
          )}
          renderOption={(params: any, option: any, state: any) => (
            <SMenuItem
              {...params}
              selected={state.selected}
              data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.option}
            >
              <ListItemText sx={{ mr: 1 }}>
                <Stack alignItems="center" direction="row" gap={1}>
                  <Avatar alt={option.fullName} src={option.picture} sx={{ width: 24, height: 24 }} />
                  <Box whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    {option.fullName}
                    <br />({option.tag})
                  </Box>
                </Stack>
              </ListItemText>
              {state.selected && (
                <ListItemIcon>
                  <Check sx={{ color: 'secondary.main' }} />
                </ListItemIcon>
              )}
            </SMenuItem>
          )}
          renderValue={(selected: any) =>
            focusedApproversContainer ? (
              <SortableList
                items={selected}
                idKey="id"
                onSortOver={() => {}}
                onSortEnd={onSortEnd}
                Element={
                  ((props: any) => (
                    <Stack
                      gap={0.5}
                      maxWidth="100%"
                      flexWrap="wrap"
                      direction="row"
                      {...props}
                      data-element={
                        elements.payModule.popup.paymentProviderDetails.approvalFlow.selectedApproversContainer
                      }
                    >
                      {props.children}
                    </Stack>
                  )) as any
                }
              >
                {({ fullName, picture, id, className, style }: any) => {
                  const ApproverAny = Approver as any;
                  return (
                    <ApproverAny
                      avatar={<Avatar alt={fullName} src={picture} />}
                      label={fullName}
                      variant="outlined"
                      onDelete={() => {
                        handleDelete(id);
                      }}
                      className={className}
                      style={style}
                      hasArrow={false}
                    />
                  );
                }}
              </SortableList>
            ) : (
              <Stack ref={approversContainerRef} direction="row" gap={0.5} flexWrap="nowrap" width="100%">
                {previewTags.map(({ picture, fullName, id }: any) => {
                  const ApproverAny = Approver as any;
                  return (
                    <ApproverAny
                      key={id}
                      avatar={<Avatar alt={fullName} src={picture} />}
                      label={fullName}
                      variant="outlined"
                      hasArrow={false}
                      sx={{ minWidth: 0 }} // always display first tag and minWidth is needed for ellipsis chip
                    />
                  );
                })}
                {!!tileSize && <Chip label={`+${tileSize}`} sx={{ backgroundColor: 'common.white' }} />}
              </Stack>
            )
          }
        />
        {!!errors[Fields.levels][i]?.[Fields.approvers] && touched[Fields.levels][i]?.[Fields.approvers] && (
          <FormHelperText
            data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.selectApproversValidationError}
            error
          >
            {errors[Fields.levels][i][Fields.approvers]}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl sx={{ minWidth: 140 }}>
        <TextFieldBase
          required
          type="float"
          error={Boolean(errors[Fields.levels][i]?.[Fields.minAmount])}
          value={levelData[Fields.minAmount]}
          name={Fields.minAmount}
          onChange={(value) => handleLevelChange(i, Fields.minAmount, value)}
          onBlur={onBlur}
          label={formatMessage({
            id: 'configurations.company.approvals.advancedGroups.form.minAmount.placeholder',
            defaultMessage: 'Min Amount',
          })}
          data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.minAmount}
        />
        {!!errors[Fields.levels][i]?.[Fields.minAmount] && (
          <FormHelperText
            data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.minAmountValidationError}
            error
          >
            {errors[Fields.levels][i][Fields.minAmount]}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl sx={{ minWidth: 140 }}>
        <InputLabel required>
          {formatMessage({
            id: 'configurations.company.approvals.advancedGroups.form.minApprovers.placeholder',
            defaultMessage: 'Min Approvers',
          })}
        </InputLabel>
        <Select
          name={Fields.minApprovers}
          label={formatMessage({
            id: 'configurations.company.approvals.advancedGroups.form.minApprovers.placeholder',
            defaultMessage: 'Min Approvers',
          })}
          value={levelData[Fields.minApprovers]}
          onChange={(e) => {
            handleLevelChange(i, Fields.minApprovers, e.target.value);
            setMustBeAllMinApproversByDefault(false);
            onBlur(e);
          }}
          data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.minApprovers}
        >
          {[...Array(approversValue.length || 1).keys()].map((n) => (
            <MenuItem
              key={n + 1}
              value={n + 1}
              data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.minApproversOption}
            >
              {approversValue.length === n + 1 || !approversValue.length
                ? formatMessage({ id: 'options.all', defaultMessage: 'All' })
                : n + 1}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 'fit-content' }} disabled>
        <ToggleButtonGroup
          value={levelData[Fields.flowType]}
          onChange={handleFlowTypeClick}
          color="primary"
          exclusive
          disabled={approversValue.length <= 1}
        >
          <ToggleButton
            value={LEVEL_FLOW_TYPE.SEQUENTIAL}
            data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.sequential}
          >
            <SvgIcon component={SequenceIcon} inheritViewBox />
            <Typography variant="subtitle2">
              {formatMessage({
                id: 'configurations.company.approvals.advancedGroups.form.flow.seq.placeholder',
                defaultMessage: 'Sequential',
              })}
            </Typography>
          </ToggleButton>
          <ToggleButton
            value={LEVEL_FLOW_TYPE.PARALLEL}
            data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.parallel}
          >
            <SvgIcon component={ParallelIcon} inheritViewBox />
            <Typography variant="subtitle2">
              {formatMessage({
                id: 'configurations.company.approvals.advancedGroups.form.flow.parallel.placeholder',
                defaultMessage: 'Parallel',
              })}
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      <IconButton
        color="error"
        onClick={() => removeLevel(i)}
        disabled={!canBeDelete}
        sx={{ mt: 1 }}
        data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.deleteLevelBtn}
      >
        <DeleteOutlineOutlinedIcon />
      </IconButton>
    </Stack>
  );
};

export default LevelItem;
